<template>
  <div class="about-wrapper">
    <div class="text-center my-10">
      <h1 class="page-title text-primary">Unser Team</h1>
      <p class="page-subtitle">Die Menschen hinter Schreinerei M²</p>
    </div>

    <!-- Group image + intro -->
    <v-img
      :lazy-src="require(`./../assets/images/Gruppe_about_lowres.jpg`)"
      transition="true"
      alt="Das Team der Schreinerei M²"
      cover
      :aspect-ratio="16/9"
      class="group-image"
      :src="require(`./../assets/images/Gruppe_about.jpg`)"
    ></v-img>
    <v-card class="bg-surface pa-6 intro-card" elevation="1">
      <p class="body-text text-justify">
        Nach langjähriger Berufserfahrung und sehr erfolgreichen Weiterbildung zum
        Schreinermeister haben wir uns entschlossen im März 2023 gemeinsam eine
        Schreinerei zu gründen. Das M steht sowohl für die Inhaber Mika Herrmann und
        Matthias Joas als auch für unsere Leidenschaft für Möbel und Massivholz.
        Unsere Produktionsstätte in Pfullingen, in der Region Reutlingen, befindet
        sich im sogenannten Schlayerareal. Für die Abwicklung unserer Aufträge
        stehen uns eine ca. 350m² große Halle, ausgestattet mit einem umfangreichen
        Maschinenpark, sowie ein Büroraum zur Auftragsplanung zur Verfügung. Wir
        übernehmen Planung, Fertigung und Montage Ihrer Ideen und Wünsche bis hin zu
        Gesamtkonzeptionen.
      </p>
    </v-card>

    <!-- Mika + Matthias -->
    <v-row class="mt-8">
      <v-col cols="12" sm="6">
        <v-card class="person-card bg-surface" elevation="1">
          <v-img
            :lazy-src="require(`./../assets/images/Mika_about_lowres.jpg`)"
            transition="true"
            alt="Mika Herrmann – Schreinermeister, Fertigung und Montage"
            cover
            :aspect-ratio="3/4"
            :src="require(`./../assets/images/Mika_about.jpg`)"
          ></v-img>
          <div class="pa-5">
            <h3 class="person-name">Mika Herrmann</h3>
            <p class="role-subtitle mb-3">Schreinermeister – Fertigung und Montage</p>
            <div class="qa-section">
              <h4 class="qa-label">Warum Schreiner?</h4>
              <p class="body-text">
                Das Arbeiten mit Holz hat mir schon im Kindesalter sehr gut gefallen. Der
                Techniklehrer meiner Schule war selbst Schreiner und hat mich somit in
                diese Richtung sehr gut fördern können. Nach der Schule und einigen
                Praktikas war für mich klar: Ich werde Schreiner. Der Job macht mir bis
                heute sehr viel Spaß, weil er Abwechslungsreich ist und man jeden Abend
                sieht was man erschaffen hat.
              </p>
            </div>
            <div class="qa-section">
              <h4 class="qa-label">Lieblings Holzart?</h4>
              <p class="body-text">
                Eiche und Esche. Persönlich gefallen sie mir am besten, wenn sie astig
                sind oder rustikal, mit einer gebürsteten Oberfläche.
              </p>
            </div>
            <div class="qa-section">
              <h4 class="qa-label">Coolstes Projekt?</h4>
              <p class="body-text">
                Vor einiger Zeit habe ich von einem Kunden jede Menge alte Eichenbalken
                bekommen. Etwa zur gleichen Zeit habe ich erfahren, dass bei mir im Ort
                eine Bankfiliale abgerissen wird, in der sich noch mehrere große
                Panzer-Glas-Platten befanden. Mir kam schnell die Idee aus den beiden
                Werkstoffen einen Esstisch für meine Wohnung zu bauen. Die Bearbeitung der
                alten Balken stellte sich als besonders anspruchsvoll heraus, da diese
                keinerlei rechte Winkel hatten. Dies erschwerte die maschinelle
                Bearbeitung sehr. Dennoch wurde nach einigen Stunden Arbeit ein Tisch
                draus.
              </p>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6">
        <v-card class="person-card bg-surface" elevation="1">
          <v-img
            :lazy-src="require(`./../assets/images/Matthias_about_lowres.jpg`)"
            transition="true"
            alt="Matthias Joas – Schreinermeister, Planung"
            cover
            :aspect-ratio="3/4"
            :src="require(`./../assets/images/Matthias_about.jpg`)"
          ></v-img>
          <div class="pa-5">
            <h3 class="person-name">Matthias Joas</h3>
            <p class="role-subtitle mb-3">Schreinermeister – Planung</p>
            <div class="qa-section">
              <h4 class="qa-label">Warum Schreiner?</h4>
              <p class="body-text">
                Mein Onkel hatte früher eine eigene Schreinerei, in der ich in den Ferien
                öfters mitarbeiten durfte. Als ich mich dann nach dem Abi für einen Beruf
                entscheiden sollte, beschloss ich eine Schreinerlehre anzufangen. Die
                vielfältige Arbeitsweise und die täglichen Herausforderungen motivieren
                mich jeden Tag.
              </p>
            </div>
            <div class="qa-section">
              <h4 class="qa-label">Lieblings Holzart?</h4>
              <p class="body-text">
                Europäischer Nussbaum. Am besten sehen hier Möbel aus, bei welchen die
                Oberfläche geölt wurde. Ein Beispiel ist das Buffet, welches unter
                Arbeiten zufinden ist.
              </p>
            </div>
            <div class="qa-section">
              <h4 class="qa-label">Coolstes Projekt?</h4>
              <p class="body-text">
                Ein Kunde wollte eine Kommode für sein Esszimmer aus Eichebalken, welche
                er aus seinem alten Dachstuhl hatte. Die alten Eichebalken mit
                schmiedeeisernen Beschlägen in eine klassische Komode zu integrieren
                stellte ein schönes Projekt dar. Der heutige Schreiner bekommt eher selten
                Aufträge für klassische Möbelstücke wie z. B. Kommoden, weshalb diese
                Projekte umso besonderer sind.
              </p>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Laura, Bene, Johannes -->
    <v-row class="mt-4 mb-8">
      <v-col cols="12" sm="4">
        <v-card class="person-card bg-surface" elevation="1">
          <v-img
            :lazy-src="require(`./../assets/images/Laura_about_lowres.jpg`)"
            transition="true"
            alt="Laura – Auszubildende bei Schreinerei M²"
            cover
            :aspect-ratio="3/4"
            :src="require(`./../assets/images/Laura_about.jpg`)"
          ></v-img>
          <div class="pa-5">
            <h3 class="person-name">Laura</h3>
            <p class="role-subtitle">Auszubildende</p>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4">
        <v-card class="person-card bg-surface" elevation="1">
          <v-img
            :lazy-src="require(`./../assets/images/Bene_about_lowres.jpg`)"
            transition="true"
            alt="Bene – Meisterschüler bei Schreinerei M²"
            cover
            :aspect-ratio="3/4"
            :src="require(`./../assets/images/Bene_about.jpg`)"
          ></v-img>
          <div class="pa-5">
            <h3 class="person-name">Bene</h3>
            <p class="role-subtitle">Meisterschüler</p>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4">
        <v-card class="person-card bg-surface" elevation="1">
          <v-img
            :lazy-src="require(`./../assets/images/Johannes_about_lowres.jpg`)"
            transition="true"
            alt="Johannes – Geselle bei Schreinerei M²"
            cover
            :aspect-ratio="3/4"
            :src="require(`./../assets/images/Johannes_about.jpg`)"
          ></v-img>
          <div class="pa-5">
            <h3 class="person-name">Johannes</h3>
            <p class="role-subtitle">Geselle</p>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: "About",
};
</script>

<style scoped>
.about-wrapper {
  max-width: 1000px;
  margin: 0 auto;
}

.page-title {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  font-size: clamp(1.5em, 5vw, 2.5em);
  font-weight: 400;
}

.page-subtitle {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  font-size: 1em;
  opacity: 0.6;
  margin-top: 0.5rem;
}

.group-image {
  border-radius: 12px 12px 0 0;
}

.intro-card {
  border-radius: 0 0 12px 12px;
  margin-top: -4px;
}

.body-text {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  font-size: 0.95em;
  line-height: 1.7;
}

.person-card {
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
}

.person-name {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  font-weight: 600;
  font-size: 1.15em;
}

.role-subtitle {
  font-size: 0.9em;
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  opacity: 0.6;
}

.qa-section {
  margin-bottom: 0.75rem;
}

.qa-section:last-child {
  margin-bottom: 0;
}

.qa-label {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  font-size: 0.9em;
  font-weight: 600;
  margin-bottom: 2px;
}
</style>
